import { Injectable, OnDestroy } from '@angular/core';
import { AuthService } from 'src/app/auth/services/auth.service';
import { SocketService } from 'src/app/services/socket.service';
import { Receiver } from 'src/app/shared/models/receiver-model';
import { CompanyUserService } from 'src/app/settings/services/company-user.service';

export interface PanelChange {
  mac: string;
  type: string;
}

interface PendingRequest {
  callback: () => void;
  key?: string;
}

/** Cada cuánto se revisa que la conexión siga viva cuando nadie toca la pantalla. */
const CONNECTION_WATCHDOG_MS = 30000;

@Injectable({
  providedIn: 'root',
})
export class WebppSocketService implements OnDestroy {
  listenEvent = this.socket.fromEvent<PanelChange>('message');
  connEvent = this.socket.fromEvent('connect');
  dcEvent = this.socket.fromEvent('disconnect');

  connected: boolean;
  callbackList: PendingRequest[] = [];

  private readonly joinedChannels = new Set<string>();
  private disconnectedAt: number | undefined;
  private watchdogIntervalId: ReturnType<typeof setInterval> | undefined;

  constructor(
    private readonly socket: SocketService,
    private readonly authService: AuthService,
    private readonly companyUserService: CompanyUserService
  ) {
    this.connected = false;
    this.dcEvent.subscribe(() => {
      // Si no se baja esta bandera, requestDataToService sigue disparando pedidos contra una
      // tubería muerta: nunca llega el 'finish', la carga se cuelga y el usuario recarga.
      this.connected = false;
      this.joinedChannels.clear();
      if (this.disconnectedAt === undefined) this.disconnectedAt = Date.now();
    });

    this.startConnectionWatchdog();
  }

  /**
   * Cuánto estuvo caída la conexión, en ms. Se consume una sola vez por reconexión: quien la lee
   * decide si el estado que tiene en pantalla quedó viejo y hay que volver a pedirlo.
   */
  consumeOutageMs(): number {
    if (this.disconnectedAt === undefined) return 0;

    const outage = Date.now() - this.disconnectedAt;
    this.disconnectedAt = undefined;
    return outage;
  }

  /**
   * Nadie reabría la conexión por su cuenta: ReceiversService llama a connect() una sola vez al
   * arrancar. En una pantalla que queda encendida sin que nadie la toque, eso deja la única salida
   * en recargar con F5.
   */
  private startConnectionWatchdog() {
    this.watchdogIntervalId = setInterval(this.reconnectIfLoggedIn, CONNECTION_WATCHDOG_MS);
    window.addEventListener('online', this.onNetworkBack);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /**
   * El handshake del socket usa un token de servicio que no vence ni identifica al usuario: si el
   * watchdog reabre la conexión después de un logout, queda un socket vivo de alguien que ya cerró
   * sesión. La sesión del usuario es la única condición para reconectar.
   */
  private readonly reconnectIfLoggedIn = () => {
    if (!this.authService.user) return;

    this.connect();
  };

  ngOnDestroy() {
    if (this.watchdogIntervalId !== undefined) {
      clearInterval(this.watchdogIntervalId);
      this.watchdogIntervalId = undefined;
    }
    window.removeEventListener('online', this.onNetworkBack);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private readonly onNetworkBack = () => this.reconnectIfLoggedIn();

  private readonly onVisibilityChange = () => {
    if (!document.hidden) this.reconnectIfLoggedIn();
  };

  /** El backend lo usa para mandar la lista sólo a este cliente en vez de a toda la sala. */
  get socketId(): string | undefined {
    return this.socket.ioSocket?.id;
  }

  connect() {
    if (!this.connected) {
      this.socket.connect();
    }
  }

  connectToReceivers(receivers) {
    if (receivers.length > 0) {
      if (!this.authService.user) return;
      if (this.companyUserService.adminHasChosenCompany()) {
        receivers = receivers.filter(
          (receiver: Receiver) => receiver.company === this.companyUserService.getCompanyIdChosenByAdmin()
        );
      }
      this.join(this.authService.user._id);
      receivers.forEach((receiver: Receiver) => {
        this.join(receiver.apiKey);
      });
      this.connected = true;

      this.drainPendingRequests();
    }
  }

  unsubscribe(receivers = []) {
    receivers.forEach((receiver: Receiver) => {
      this.leave(receiver.apiKey);
    });
  }

  close() {
    this.leaveAllChannels();
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.connected = false;
    this.clearPendingRequests();
  }

  clearPendingRequests() {
    this.callbackList = [];
  }

  /**
   * Corta la conexión y la vuelve a abrir. Para cuando el socket se declara conectado pero la
   * tubería está trabada: reintentar el pedido sobre la misma conexión no lleva a ninguna parte.
   * No limpia la cola, porque el pedido que la disparó tiene que sobrevivir a la reconexión.
   */
  cutAndReconnect() {
    this.leaveAllChannels();
    this.connected = false;
    this.socket.disconnect();
    this.socket.connect();
  }

  requestDataToService = (callback: () => void, key?: string) => {
    if (this.connected) {
      callback();
      return;
    }

    // Sin esto una ráfaga de recargas encola la misma carga muchas veces y todas se disparan
    // juntas cuando el socket vuelve.
    if (key && this.callbackList.some((pending) => pending.key === key)) return;

    this.callbackList.push({ callback, key });
  };

  private join(channel: string) {
    if (!channel) return;

    this.joinedChannels.add(channel);
    this.socket.emit('join', channel);
  }

  private leave(channel: string) {
    if (!channel) return;

    this.joinedChannels.delete(channel);
    this.socket.emit('leave', channel);
  }

  private leaveAllChannels() {
    this.joinedChannels.forEach((channel) => this.socket.emit('leave', channel));
    this.joinedChannels.clear();
  }

  private drainPendingRequests() {
    if (this.callbackList.length === 0) return;

    const pending = this.callbackList;
    this.callbackList = [];
    pending.forEach(({ callback }) => callback());
  }
}
